<template>
  <v-card>
    <v-card-title>
      Split Document
      <v-tooltip location="bottom">
        <template #activator="{ props: aprops }">
          <v-icon size="x-small" v-bind="aprops" icon="mdi-information-outline" />
        </template>
        <span>
          Split the document into multiple documents, by selecting pages and moving them.
          <br />
          The document will be processed in the background. Split documents will appear in Inbox.
        </span>
      </v-tooltip>
    </v-card-title>
    <v-alert v-if="errorMsg" class="error-msg" :text="errorMsg" type="error" variant="outlined"></v-alert>
    <v-container fluid class="overflow-x-auto">
      <v-row class="flex-nowrap">
        <v-col v-for="splitNum in pageBySplit.length + 1" :key="splitNum" :cols="colSize" :md="colSizeMd">
          <div class="ps-2 w-100">Document {{ splitNum }}</div>
        </v-col>
      </v-row>
      <v-row class="flex-nowrap">
        <v-col
          v-for="splitNum in pageBySplit.length + 1"
          :key="splitNum"
          :cols="colSize"
          :md="colSizeMd"
          class="border-e-thin">
          <template v-if="splitNum <= pageBySplit.length">
            <div v-for="page in pageBySplit[splitNum - 1]" :key="page.id" class="d-inline-block w-25">
              <img
                :src="page.imagePresignedUrl"
                alt=""
                class="w-100 px-1"
                :style="{ transform: `rotate(${page.rotationDegrees ?? 0}deg)` }"
                @click="onPreviewClick(page)" />
              <v-checkbox
                :label="`Page ${page.pageNumber}`"
                :model-value="checkedByPageNumber[page.pageNumber]"
                @change="(e) => onPageChecked(page, e)" />
            </div>
          </template>
          <template v-else>
            <div class="d-flex justify-center align-center">
              <h2 class="text-center text-grey">Empty</h2>
            </div>
          </template>
        </v-col>
      </v-row>
      <v-row class="flex-nowrap">
        <v-col v-for="splitNum in pageBySplit.length + 1" :key="splitNum" :cols="colSize" :md="colSizeMd">
          <v-btn-lh
            v-if="splitNum <= pageBySplit.length"
            variant="outlined"
            :disabled="checkedPages.length === 0"
            :loading="loading"
            @click="onMoveToSplit(splitNum)">
            Add to this split
          </v-btn-lh>
          <!-- Max splits -->
          <v-btn-lh
            v-else-if="splitNum < 21"
            variant="outlined"
            :disabled="checkedPages.length === 0"
            :loading="loading"
            @click="onMoveToSplit(splitNum)">
            Move to new split
          </v-btn-lh>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-btn-lh class="me-2" :disabled="pageBySplit.length === 1" :loading="loading" @click="onDone">
            Done, Split and Process
          </v-btn-lh>
          <v-btn-lh color="error" variant="text" :loading="loading" @click="props.onClose(false)">Cancel</v-btn-lh>
        </v-col>
      </v-row>
    </v-container>
  </v-card>
  <v-dialog v-model="showConfirmationDialog" width="auto">
    <v-card>
      <v-card-title> Are you sure you want to split the document? </v-card-title>
      <v-card-text>
        This will create {{ pageBySplit.length }} new documents, which will be processed in the background.
      </v-card-text>
      <v-card-actions>
        <v-btn-lh class="me-2" :loading="loading" @click="onDoneConfirmation">Yes, split the document</v-btn-lh>
        <v-btn-lh color="error" variant="text" :loading="loading" @click="() => (showConfirmationDialog = false)">
          Cancel
        </v-btn-lh>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog :model-value="selectedPreviewPage !== undefined" @click:outside="selectedPreviewPage = undefined">
    <span class="d-flex justify-center align-center" @click="selectedPreviewPage = undefined">
      <img
        :src="selectedPreviewPage?.imagePresignedUrl"
        alt=""
        :style="{
          maxHeight: '90vh',
          transform: `rotate(${selectedPreviewPage?.rotationDegrees ?? 0}deg)`,
        }"
        @click.stop />
    </span>
  </v-dialog>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useIntakeStore } from "@/stores/intakeStore";
import { intakeService } from "@/services/intakeService";

const props = defineProps({
  onClose: {
    required: true,
    type: Function,
  },
});

const colSize = ref(8);
const colSizeMd = ref(5);
const intakeStore = useIntakeStore();
const showConfirmationDialog = ref(false);
const selectedPreviewPage = ref(undefined);
const loading = ref(false);
const errorMsg = ref(undefined);

// This should be an array of arrays, each array is a split of the pages
const pageBySplit = ref([]);
const checkedPages = ref([]);
const checkedByPageNumber = computed(() => {
  return checkedPages.value.reduce((acc, page) => {
    acc[page.pageNumber] = true;
    return acc;
  }, {});
});

defineExpose({
  checkedPages,
});

onMounted(() => {
  // Start with the original document with all pages
  pageBySplit.value = [[...intakeStore.pageManager?.pages]];
});

const onMoveToSplit = (splitNum) => {
  // First pop from other pages
  pageBySplit.value = pageBySplit.value.map((split) => {
    return split.filter((page) => !checkedPages.value.includes(page));
  });
  if (splitNum > pageBySplit.value.length) {
    pageBySplit.value.push([]);
  }
  pageBySplit.value[splitNum - 1].push(...checkedPages.value);
  pageBySplit.value[splitNum - 1] = pageBySplit.value[splitNum - 1].sort((a, b) => a.pageNumber - b.pageNumber);
  pageBySplit.value = pageBySplit.value.filter((split) => split.length > 0);
  checkedPages.value = [];
};

const onPageChecked = (page, event) => {
  if (event.target.checked) {
    checkedPages.value.push(page);
  } else {
    checkedPages.value = checkedPages.value.filter((p) => p !== page);
  }
};

const onPreviewClick = (page) => {
  selectedPreviewPage.value = page;
};

const onDone = () => {
  showConfirmationDialog.value = true;
};

const onDoneConfirmation = async () => {
  try {
    errorMsg.value = undefined;
    loading.value = true;
    await intakeService.postIntakeDocumentSplit(intakeStore.intakeDocument.documentKey, {
      pageIdsSplits: pageBySplit.value.map((split) => split.map((page) => page.id)),
    });
    props.onClose(true);
  } catch (error) {
    errorMsg.value = error.message;
  } finally {
    showConfirmationDialog.value = false;
    loading.value = false;
  }
};
</script>
